import { useEffect, useState, useRef } from "react";
import { fetchWithAuth } from "../../../../../../api-client/api_client";
import { CommonModelDiffRunExecution, CommonModelDiffRunStatus } from "../entities";
import { showErrorToast } from "../../../../../shared/Toasts";

interface GetCommonModelDiffRunsProps {
  blueprintVersionID: string;
  pollingInterval?: number;
}

export const useGetCommonModelDiffRuns = ({
  blueprintVersionID,
  pollingInterval = 15000,
}: GetCommonModelDiffRunsProps) => {
  const [commonModelDiffRunExecutions, setCommonModelDiffRunExecutions] = useState<
    CommonModelDiffRunExecution[] | null
  >(null);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchData = () => {
    if (!blueprintVersionID) {
      setCommonModelDiffRunExecutions([]);
      setIsInitialLoad(false);
      return;
    }

    fetchWithAuth({
      path: `/blueprints/${blueprintVersionID}/common-model-diff-runs`,
      method: "GET",
      body: {},
      onResponse: (data: CommonModelDiffRunExecution[]) => {
        setCommonModelDiffRunExecutions(data);
        setIsInitialLoad(false);

        // Check if any executions are in PENDING or IN_PROGRESS state
        const hasInProgressExecutions = data.some(
          (execution) =>
            execution.status === CommonModelDiffRunStatus.PENDING ||
            execution.status === CommonModelDiffRunStatus.IN_PROGRESS
        );

        // Start or stop polling based on execution states
        if (hasInProgressExecutions) {
          // Only start polling if we don't have an active interval
          if (!intervalRef.current) {
            intervalRef.current = setInterval(fetchData, pollingInterval);
          }
        } else {
          // If no executions are in progress, clear the interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      },
      onError: (error) => {
        setIsInitialLoad(false);
        error
          ?.json()
          .then((value: string | Record<string, any>) => {
            let errorMessage = "";
            if (typeof value == "string") {
              errorMessage = value;
            } else {
              errorMessage = value?.non_field_errors ?? JSON.stringify(value);
            }
            showErrorToast(
              `Failed to fetch Common Model diff executions. Error message: \n ${errorMessage}`
            );
          })
          .catch(() => {
            showErrorToast("Failed to fetch Common Model diff executions");
          });
      },
    });
  };

  useEffect(() => {
    // Initial fetch
    fetchData();

    // Clean up interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [blueprintVersionID, pollingInterval]);

  return {
    commonModelDiffRunExecutions,
    setCommonModelDiffRunExecutions,
    isInitialLoad,
    fetchData,
  };
};
